
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Copy, ArrowLeft, Bot } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { Database } from "@/integrations/supabase/types";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sanitizePromptVariable } from "@/utils/sanitize";

type Prompt = Database["public"]["Tables"]["prompts"]["Row"];

const PromptDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [processedContent, setProcessedContent] = useState("");

  useEffect(() => {
    const fetchPrompt = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from("prompts")
          .select("*")
          .eq("id", id)
          .single();
          
        if (error) throw error;
        
        setPrompt(data);
        setProcessedContent(data.conteudo);
        
        // Initialize variables from prompt.variaveis
        if (data.variaveis) {
          const vars = data.variaveis as Record<string, string>;
          setVariables(vars);
        }
      } catch (error) {
        console.error("Error fetching prompt:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar o prompt",
          variant: "destructive",
        });
        navigate("/dashboard/prompts");
      } finally {
        // Pequeno atraso para garantir que todos os dados foram processados
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };
    
    fetchPrompt();
  }, [id, navigate]);
  
  useEffect(() => {

    if (prompt?.conteudo) {
      let content = prompt.conteudo;
      Object.entries(variables).forEach(([key, value]) => {
        const sanitizedVal = sanitizePromptVariable(value || "");
        // Escape special characters in the key for safe use in a regex
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Construct the regex to find "[KEY]" (e.g., "[VARA]", "[DATA DO CASAMENTO]")
        const regex = new RegExp(`\\[${escapedKey}\\]`, "g");
        
        content = content.replace(regex, sanitizedVal);
      });
      setProcessedContent(content);
    } else if (prompt) {
      setProcessedContent(prompt.conteudo || "");
    }
  }, [variables, prompt]);

  
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(processedContent);
      
      // Increment usage count
      if (prompt) {
        await supabase.rpc("increment_prompt_use" as any, {
          prompt_id: prompt.id
        });
      }
      
      toast({
        title: "Copiado!",
        description: "O prompt foi copiado para a área de transferência",
      });
    } catch (error) {
      toast({
        title: "Erro ao copiar",
        description: "Não foi possível copiar o prompt",
        variant: "destructive",
      });
    }
  };


  const openChatGPTWithPrompt = async () => {
    try {
      // Verifica se o prompt existe antes de prosseguir
      if (!prompt) {
        toast({
          title: "Erro",
          description: "Prompt não encontrado",
          variant: "destructive",
        });
        return;
      }

      // Copia o prompt para a área de transferência
      await navigator.clipboard.writeText(processedContent);
      
      // Incrementa o contador de uso
      await supabase.rpc("increment_prompt_use" as any, {
        prompt_id: prompt.id
      });
      
      // Abre o ChatGPT em uma nova aba
      window.open('https://chatgpt.com/', '_blank');
      
      toast({
        title: "Prompt copiado!",
        description: "O prompt foi copiado e o ChatGPT foi aberto. Cole o prompt no chat.",
      });
    } catch (error) {
      console.error("Erro ao abrir ChatGPT com prompt:", error);
      toast({
        title: "Erro",
        description: "Não foi possível copiar o prompt ou abrir o ChatGPT",
        variant: "destructive",
      });
    }
  };
  
  const handleVariableChange = (key: string, value: string) => {
    setVariables(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <DashboardLayout isLoading={isLoading}>
      {!isLoading && prompt && (
        <div>
          <Button
            variant="ghost"
            className="mb-4 dark:text-white"
            onClick={() => navigate("/dashboard/prompts")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Voltar para Prompts
          </Button>

          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold dark:text-white">{prompt.titulo}</h1>
              <Badge className=" bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]">{prompt.area}</Badge>
            </div>
            <p className="text-gray-600 mt-2">{prompt.descricao}</p>
            <div className="text-sm text-gray-500 mt-1">
              {prompt.total_uso} usos • Atualizado em{" "}
              {prompt.atualizado_em && new Date(prompt.atualizado_em).toLocaleDateString("pt-BR")}
            </div>
          </div>

          {prompt.variaveis && Object.keys(prompt.variaveis).length > 0 && (
            <Card className="mb-6 dark:bg-white">
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-3 dark:text-gray-700">
                  Personalize seu prompt
                </h3>
                <div className="grid gap-4">
                  {Object.entries(variables).map(([key, value]) => (
                    <div key={key}>
                      <Label htmlFor={key} className="dark:text-gray-400">{key}</Label>
                      <Input
                        id={key}
                        value={value}
                        onChange={(e) => handleVariableChange(key, e.target.value)}
                        className="mt-1 dark:bg-white dark:text-gray-700"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 dark:text-white">Conteúdo do Prompt</h3>
            <div className="relative">
              <Textarea
                value={processedContent}
                readOnly
                className="min-h-[200px] dark:bg-white dark:text-gray-700"
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openChatGPTWithPrompt}
                className="dark:bg-[#072a45] dark:hover:bg-[#072a45] dark:text-white absolute top-2 right-28"
                title="Copiar prompt e abrir ChatGPT"
              >
                <Bot className="h-4 w-4 mr-1" />
              </Button>
              <Button
                className="absolute top-2 right-2 flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]"
                size="sm"
                onClick={copyToClipboard}
              >
                <Copy className="h-4 w-4 mr-1" /> Copiar
              </Button>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2 dark:text-white">Instruções de Uso</h3>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-gray-700">{prompt.instrucoes}</p>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !prompt && (
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">Prompt não encontrado</h2>
          <p className="text-gray-600 mb-4">
            O prompt que você está procurando não existe ou foi removido.
          </p>
          <Button onClick={() => navigate("/dashboard/prompts")} className="dark:text-white">
            Voltar para Prompts
          </Button>
        </div>
      )}
    </DashboardLayout>
  );
};

export default PromptDetail;
